import {
  FocusEventHandler,
  FocusEvent,
  ReactNode,
  useState,
  useCallback,
  KeyboardEventHandler,
  ChangeEvent,
  useEffect,
  useMemo,
  useId,
} from 'react';

import classNames from 'classnames';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';

import { useLogger } from '../../services/hooks';
import { Typography } from '../../Typography';
import { Box } from '../../Box';
import { Label, Preloader, ClearButton } from '../common';
import InputMask from '../../ReactInputMask';

import { ShowPasswordButton } from './ShowPasswordButton';
import useStyles from './TextInput.styles';

const EXCLUDED_INPUT_NUMBER_KEYS = ['e', 'E'];

const handleOnWheel: EventListener = (e) => e.preventDefault();

/**
 * Свойства компонента {@link TextInput}
 */
export interface TextInputProps {
  /**
   * Атрибут ID для поля ввода.
   */
  id?: string;

  /**
   * Атрибут name для поля ввода.
   */
  name?: string;

  /**
   * Тип поля ввода (например, 'text', 'password').
   */
  type?: string;

  /**
   * Текст метки для поля ввода.
   */
  label?: string;

  /**
   * Иконка для метки.
   */
  labelIcon?: ReactNode;

  /**
   * Текущее значение поля ввода.
   */
  value?: string | undefined;

  /**
   * Текст плейсхолдера для поля ввода.
   */
  placeholder?: string;

  /**
   * Функция для предобработки значения перед применением maxLength и onChange.
   * Используется для фильтрации/очистки ввода.
   */
  inputFilter?: (value: string | undefined) => string | undefined;
  /**
   * Функция обратного вызова для обработки изменений значения ввода.
   */
  onChange: ((value: string | undefined) => void) | ((value: string) => void);

  /**
   * Функция обратного вызова для обработки событий фокуса.
   */
  onFocus?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;

  /**
   * Функция обратного вызова для обработки событий потери фокуса.
   */
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;

  /**
   * Функция обратного вызова для обработки событий нажатия клавиш.
   */
  onKeyPress?: KeyboardEventHandler<HTMLInputElement>;

  /**
   * Содержимое всплывающей подсказки, отображаемой с меткой.
   */
  tooltip?: ReactNode | null;

  /**
   * Шаблон маски для поля ввода.
   */
  mask?: string;

  /**
   * Если true, поле ввода будет отмечено как имеющее ошибку.
   */
  error?: boolean;

  /**
   * Вспомогательный текст, отображаемый под полем ввода.
   */
  helperText?: string;

  /**
   * Если true, поле ввода будет отмечено как обязательное.
   */
  required?: boolean;

  /**
   * Если true, компонент будет отключен.
   */
  disabled?: boolean;

  /**
   * Если true, компонент будет показывать индикатор загрузки.
   */
  loading?: boolean;

  /**
   * Если true, поле ввода будет текстовой областью с несколькими строками.
   */
  multiline?: boolean;

  /**
   * Если true, кнопка очистки будет отображаться, когда поле ввода имеет значение.
   */
  clearable?: boolean;

  /**
   * Количество строк для текстовой области с несколькими строками.
   */
  rows?: number;

  /**
   * Максимальное количество строк для текстовой области.
   */
  maxRows?: number;

  /**
   * Атрибут autocomplete для поля ввода.
   */
  autocomplete?: string;

  /**
   * Режим ввода для поля ввода (например, 'search', 'numeric').
   */
  inputMode?:
    | 'search'
    | 'text'
    | 'none'
    | 'tel'
    | 'url'
    | 'email'
    | 'numeric'
    | 'decimal';

  /**
   * Тестовый ID для компонента.
   */
  testId?: string;

  /**
   * Предупреждающий текст.
   */
  warning?: string;

  /**
   * Максимальная длина ввода.
   */
  maxLength?: number;

  /**
   * Минимальная длина ввода.
   */
  minLength?: number;
}

/**
 * TextInput - это универсальный компонент поля ввода, который поддерживает различные типы
 * ввода, включая маскированный ввод, переключение видимости пароля, состояние загрузки
 * и возможность очистки ввода.
 *
 * @public
 * @param id - Атрибут ID для поля ввода.
 * @param name - Атрибут name для поля ввода.
 * @param type - Тип поля ввода (например, 'text', 'password').
 * @param label - Текст метки для поля ввода.
 * @param labelIcon - Иконка для метки.
 * @param value - Текущее значение поля ввода.
 * @param placeholder - Текст плейсхолдера для поля ввода.
 * @param inputFilter - Функция для предобработки значения перед применением maxLength и onChange.
 * @param onChange - Функция обратного вызова для обработки изменений значения ввода.
 * @param onFocus - Функция обратного вызова для обработки событий фокуса.
 * @param onBlur - Функция обратного вызова для обработки событий потери фокуса.
 * @param onKeyPress - Функция обратного вызова для обработки событий нажатия клавиш.
 * @param tooltip - Содержимое всплывающей подсказки, отображаемой с меткой.
 * @param mask - Шаблон маски для поля ввода.
 * @param error - Если true, поле ввода будет отмечено как имеющее ошибку.
 * @param helperText - Вспомогательный текст, отображаемый под полем ввода.
 * @param required - Если true, поле ввода будет отмечено как обязательное.
 * @param disabled - Если true, компонент будет отключен.
 * @param loading - Если true, компонент будет показывать индикатор загрузки.
 * @param multiline - Если true, поле ввода будет текстовой областью с несколькими строками.
 * @param clearable - Если true, кнопка очистки будет отображаться, когда поле ввода имеет значение.
 * @param rows - Количество строк для текстовой области с несколькими строками.
 * @param maxRows - Максимальное количество строк для текстовой области.
 * @param autocomplete - Атрибут autocomplete для поля ввода.
 * @param inputMode - Режим ввода для поля ввода (например, 'search', 'numeric').
 * @param testId - Тестовый ID для компонента.
 * @param warning - Предупреждающий текст.
 * @param maxLength - Максимальная длина ввода.
 * @param minLength - Минимальная длина ввода.
 * @returns JSX компонента.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link TextInputProps}
 */
export const TextInput = ({
  id,
  type = 'text',
  label,
  labelIcon,
  value,
  placeholder,
  inputFilter,
  onChange,
  onFocus,
  onBlur,
  onKeyPress,
  tooltip = null,
  mask,
  error = false,
  helperText = '',
  required = false,
  disabled = false,
  loading = false,
  multiline = false,
  clearable = false,
  testId,
  rows = 1,
  maxRows,
  inputMode,
  autocomplete,
  warning,
  maxLength,
  minLength,
  ...rest
  // eslint-disable-next-line complexity
}: TextInputProps) => {
  const { classes } = useStyles();
  const [showPassword, setShowPassword] = useState(false);
  const logger = useLogger();
  const reactId = useId();
  const inputId = useMemo(() => {
    return id ?? reactId;
  }, [id, reactId]);

  const handleToggleShowPassword = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      let newValue = e.target.value;

      if (inputFilter) {
        newValue = inputFilter(newValue) ?? '';
      }

      if (maxLength && newValue) {
        newValue = newValue.slice(0, maxLength);
      }
      onChange(newValue);
    },
    [inputFilter, onChange, maxLength],
  );

  const handleOnFocus = (event: FocusEvent<HTMLInputElement>) => {
    onFocus?.(event);

    if (type === 'number') {
      event.target.addEventListener('wheel', handleOnWheel);
    }
  };

  const handleOnBlur = (event: FocusEvent<HTMLInputElement>) => {
    onBlur?.(event);

    if (type === 'number') {
      event.target.removeEventListener('wheel', handleOnWheel);
    }
  };

  const handleOnKeyPress: KeyboardEventHandler<HTMLInputElement> = (event) => {
    onKeyPress?.(event);

    if (type === 'number' && EXCLUDED_INPUT_NUMBER_KEYS.includes(event.key)) {
      event.preventDefault();
    }
  };

  /**
   * Функция для рендеринга текста помощи.
   *
   * @param props - Свойства текстового поля.
   * @returns  - JSX элемент для текста помощи или undefined.
   */
  const renderHelperText = useCallback(
    ({
      isError,
      text,
      warningText,
      maxInputLength,
      minInputLength,
    }: {
      isError: boolean;
      warningText?: string;
      text: string;
      maxInputLength?: number;
      minInputLength?: number;
    }) => {
      let lengthHelperText;
      if (minInputLength && maxInputLength) {
        if (minInputLength === maxInputLength) {
          lengthHelperText = `Количество символов: ${minInputLength}`;
        } else {
          lengthHelperText = `Допустимо от ${minInputLength} до ${maxInputLength} символов`;
        }
      } else if (minInputLength) {
        lengthHelperText = `Минимальное количество символов: ${minInputLength}`;
      } else if (maxInputLength) {
        lengthHelperText = 'Количество символов ограничено';
      }

      const result = isError ? text : lengthHelperText || helperText;

      if (!isError && warningText) {
        return (
          <Typography variant="caption" color="text.light.secondary">
            {warning}
          </Typography>
        );
      }

      if (result) {
        return (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography
              variant="caption"
              color={isError ? 'accent.text.error' : 'text.light.secondary'}
            >
              {result}
            </Typography>
            {(!isError || text === lengthHelperText) &&
              maxInputLength &&
              maxInputLength !== minInputLength && (
                <Typography
                  variant="caption"
                  color={isError ? 'accent.text.error' : 'text.light.secondary'}
                >
                  {`${value?.length || 0}/${maxInputLength}`}
                </Typography>
              )}
          </Box>
        );
      }

      return helperText;
    },
    [value?.length, warning, helperText],
  );

  useEffect(() => {
    if (
      'OTPCredential' in window &&
      'navigator' in window &&
      'credentials' in window.navigator &&
      autocomplete === 'one-time-code'
    ) {
      const ac = new AbortController();
      const signalAbortMessage = 'signal is aborted without reason';

      window?.navigator?.credentials
        ?.get({
          // @ts-ignore
          otp: { transport: ['sms'] },
          signal: ac.signal,
        })
        .then((otp) => {
          // @ts-ignore;
          Promise.resolve(otp?.code).then((code) => {
            onChange(code);
          });
        })
        .catch((e) => {
          if (e.message !== signalAbortMessage)
            logger.warning(e, {
              tags: {
                vkdoc_error_type: 'webotp',
              },
            });
        });

      return () => {
        ac.abort();
      };
    }
  }, [autocomplete, logger, onChange]);

  const buttons =
    type === 'password' ? (
      <ShowPasswordButton
        isShow={showPassword}
        disabled={disabled}
        error={error}
        onClick={handleToggleShowPassword}
      />
    ) : (
      !multiline &&
      !disabled &&
      clearable &&
      value && (
        <ClearButton
          className={classes.clearButton}
          onClick={() => onChange('')}
          color={error ? 'error' : 'disabled'}
          testId="text-input-clear"
        />
      )
    );

  return (
    <FormControl className={classNames(classes.textInput, 'aqa_text_input')}>
      {label && (
        <Label
          label={label}
          required={required}
          tooltip={tooltip}
          icon={labelIcon}
          inputId={inputId}
        />
      )}

      {mask ? (
        <InputMask
          {...rest}
          mask={mask}
          value={value}
          onChange={handleChange}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyPress={onKeyPress}
          disabled={disabled || loading}
        >
          <TextField
            id={inputId}
            name={rest.name}
            autoComplete={autocomplete || 'off'}
            inputMode={inputMode}
            data-testid={testId}
            classes={{ root: classes.input }}
            variant="outlined"
            fullWidth
            placeholder={placeholder || label}
            error={error}
            disabled={disabled || loading}
            slotProps={{
              input: {
                className: 'textFieldInput aqa_input',
                classes: { adornedEnd: classes.adornedEnd },
                endAdornment: loading ? <Preloader /> : buttons,
              },
              ...(maxLength
                ? {
                    htmlInput: {
                      maxLength,
                    },
                  }
                : {}),
            }}
            helperText={renderHelperText({
              isError: error,
              text: helperText,
              warningText: warning,
              maxInputLength: maxLength,
              minInputLength: minLength,
            })}
          />
        </InputMask>
      ) : (
        <TextField
          {...rest}
          id={inputId}
          autoComplete={autocomplete || 'off'}
          data-testid={testId}
          classes={{ root: classes.input }}
          type={showPassword ? 'text' : type}
          inputMode={inputMode}
          value={
            loading && (type !== 'password' || showPassword)
              ? 'Загрузка...'
              : value
          }
          onChange={handleChange}
          onFocus={handleOnFocus}
          onBlur={handleOnBlur}
          onKeyPress={handleOnKeyPress}
          variant="outlined"
          fullWidth
          placeholder={placeholder || label}
          error={error}
          disabled={disabled || loading}
          multiline={multiline}
          minRows={rows}
          maxRows={maxRows}
          slotProps={{
            input: {
              id: inputId,
              className: 'textFieldInput aqa_input',
              classes: { adornedEnd: classes.adornedEnd },
              endAdornment: loading ? <Preloader /> : buttons,
            },
          }}
          helperText={renderHelperText({
            isError: error,
            text: helperText,
            warningText: warning,
            maxInputLength: maxLength,
            minInputLength: minLength,
          })}
        />
      )}
    </FormControl>
  );
};
